import { useCallback, useEffect, useRef, useState } from 'react';
import { Clipboard, FileUp, ImageUp, ShieldCheck } from 'lucide-react';
import { MAX_BATCH_IMAGES, MAX_PDF_PAGES } from '../../lib/ocr/constants';

export const UploadZone = ({ onFiles }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [pasteNote, setPasteNote] = useState('');

  const handleFiles = useCallback(
    (files) => {
      if (files && files.length) onFiles(files);
    },
    [onFiles],
  );

  useEffect(() => {
    const onPaste = (event) => {
      const items = event.clipboardData?.files;
      if (items && items.length) {
        handleFiles(items);
        setPasteNote(`Pasted ${items.length} file${items.length > 1 ? 's' : ''} from the clipboard.`);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFiles]);

  const openPicker = () => inputRef.current?.click();

  return (
    <section aria-labelledby="upload-heading" className="scroll-mt-24" id="upload">
      <h2 className="sr-only" id="upload-heading">
        Add images or PDFs
      </h2>
      <div
        data-testid="upload-dropzone"
        role="button"
        tabIndex={0}
        aria-label="Add files. Drag and drop, press Enter to browse, or paste an image with Control V."
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer?.files);
        }}
        className={`surface-card cursor-pointer px-5 py-9 text-center transition-colors duration-200 sm:px-10 sm:py-14 ${
          dragging ? 'border-indigo-400 bg-[#eef2ff]' : 'hover:bg-[#fafbff]'
        }`}
      >
        <span className="brand-gradient mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg shadow-indigo-500/25">
          <ImageUp aria-hidden="true" className="h-7 w-7" />
        </span>
        <p className="font-display text-lg font-bold text-slate-900 sm:text-xl">
          Drop images or a PDF here
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Or tap to browse your device. On desktop you can also paste an image with{' '}
          <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs font-semibold">
            Ctrl
          </kbd>{' '}
          +{' '}
          <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs font-semibold">
            V
          </kbd>
          .
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
          <span className="rounded-full bg-[#f1f3fc] px-3 py-1 font-medium">JPG · JPEG · PNG · WEBP · BMP</span>
          <span className="rounded-full bg-[#f1f3fc] px-3 py-1 font-medium">PDF up to {MAX_PDF_PAGES} pages</span>
          <span className="rounded-full bg-[#f1f3fc] px-3 py-1 font-medium">Images ≤ 10 MB · PDFs ≤ 20 MB</span>
          <span className="rounded-full bg-[#f1f3fc] px-3 py-1 font-medium">Up to {MAX_BATCH_IMAGES} images per batch</span>
        </div>
        <input
          ref={inputRef}
          data-testid="upload-file-input"
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.bmp,.pdf,image/jpeg,image/jpg,image/png,image/webp,image/bmp,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 text-emerald-600" />
          Nothing leaves your device
        </span>
        <span className="inline-flex items-center gap-1.5">
          <FileUp aria-hidden="true" className="h-3.5 w-3.5" />
          Add several files at once
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clipboard aria-hidden="true" className="h-3.5 w-3.5" />
          Clipboard paste supported
        </span>
      </div>
      {pasteNote ? (
        <p aria-live="polite" className="mt-2 text-xs font-medium text-indigo-700" data-testid="paste-note">
          {pasteNote}
        </p>
      ) : null}
    </section>
  );
};

export default UploadZone;
